import React, { useContext, useEffect, useState, useRef } from "react";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import arrImg from "@/assets/cst_whtArrow.png";
import { bankAccountSchema } from "../../../utils/error_schema";
import { checkFormError, isObjectValueEmpty } from "@/utils";
import { FloatingField, SelectField } from "../../Common";
import DropzoneField from "../../Common/DropzoneField";
import {
  banksRequest,
  citiesRequest,
  getSettlementBankAccounts,
  createBankAccountRequest,
  updateBankAccountRequest,
  validateIban
} from "../../../services";
import Loader from "../../Common/Loader";
import { custom_context } from "../../../utils/custom_context";
import { toast } from "react-toastify";
import "../KYC/kyc.css";
import { useParams } from "react-router-dom";

export const BankDetailsUpdateModal = ({ evaluationDetails, showModal, onHide }) => {
  const { applicationId } = useParams();
  const { profileData } = useContext(custom_context.ProfileData);
  const companyName = profileData?.company_details?.company_name;
  const [cityList, setcityList] = useState([]);
  const [bankList, setbankList] = useState([]);
  const [bankAccountDetails, setBankAccountDetails] = useState({});
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState(null); // null, 'success', 'failed'
  const [requireProof, setRequireProof] = useState(true);

  const getCities = async () => {
    const resp = await citiesRequest();
    if (resp?.status) {
      setcityList(resp?.results);
    }
  };

  const getBanks = async () => {
    const resp = await banksRequest();
    if (resp?.status) {
      setbankList(resp?.results);
    }
  };

  const getBankAccountDetails = async () => {
    const resp = await getSettlementBankAccounts({ loan_id: applicationId });
    if (resp?.status) {
      setBankAccountDetails(resp?.results[0]);
    }
  }

  useEffect(() => {
    getBanks();
    getCities();
    getBankAccountDetails();
  }, []);

  const initialValue = {
    merchant: profileData?.id,
    account_holder_name: '',
    bank: "",
    city: "",
    account_number: "",
    swift_code: "",
    bank_verification_proof: "",
    iban_verified: false,
  };
  const [inputValue, setInputValue] = useState(!!bankAccountDetails?.id ? bankAccountDetails : initialValue);
  const [inputError, setInputError] = useState("");
  const [showResponseError, setResError] = useState();
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  // Synchronous guard: state updates aren't immediate, so a fast double-click
  // could otherwise submit twice before `isLoading` disables the button.
  const submittingRef = useRef(false);

  const formDisabled =
    bankAccountDetails?.status &&
    ["under_review", "verified"].includes(bankAccountDetails.status);

  useEffect(() => {
    if (bankAccountDetails?.id) {
      if (bankList.length && cityList.length) {
        const bankLabel = bankList.find(
          (item) => item.id == bankAccountDetails?.bank
        );
        const cityLabel = cityList.find((value) => value.id == bankAccountDetails?.city);
        setInputValue((s) => {
          const obj = {
            ...s,
            ...bankAccountDetails,
            city: cityLabel?.name
              ? {
                value: cityLabel?.id,
                label: cityLabel?.name,
              }
              : null,
            bank: bankLabel?.name
              ? {
                value: bankLabel?.id,
                label: bankLabel?.name,
              }
              : null,
          };
          return obj;
        });
      }
    }
  }, [bankAccountDetails, cityList, bankList]);

  const verifyBankAccount = async () => {
    if (!inputValue.account_number) {
      toast.error("Please enter IBAN number first");
      return;
    }

    setIsVerifying(true);
    setVerificationStatus(null);

    try {
      const response = await validateIban({
        account_number: inputValue.account_number,
      });

      if (response.success && response.results.is_active) {
        setVerificationStatus('success');
        setInputValue(prev => ({
          ...prev,
          iban_verified: true,
          iban_verification_response: response, // Store the raw response
          account_holder_name: response.results.name || prev.account_holder_name
        }));

        if (response.results.name) {
          toast.success("IBAN verified successfully. Account holder name auto-populated.");
        } else {
          toast.success("IBAN verified successfully. Please enter account holder name.");
        }
      } else {
        setVerificationStatus('failed');
        setRequireProof(true);
        setInputValue(prev => ({
          ...prev,
          iban_verified: false,
          iban_verification_response: response // Store the raw response even if failed
        }));
        toast.error("IBAN verification failed. Please enter the account holder name manually.");
      }
    } catch (error) {
      setVerificationStatus('failed');
      setRequireProof(true);
      setInputValue(prev => ({
        ...prev,
        iban_verified: false,
        iban_verification_response: error.response?.data || { error: "Verification failed" }
      }));
      toast.error("IBAN verification failed. Please enter the account holder name manually.");
    } finally {
      setIsVerifying(false);
    }
  };

  const onSubmitDetails = async (e) => {
    if (formDisabled) {
      onHide();
      return;
    }

    e.preventDefault();

    // Ignore accidental double-clicks while a submit is already in flight.
    if (submittingRef.current) return;
    submittingRef.current = true;
    setIsLoading(true);

    try {
      if (!inputValue.bank_account_proof) {
        toast.error("Please upload proof of account ownership");
        return;
      }

      const validationResult = await checkFormError(inputValue, bankAccountSchema);
      if (validationResult !== true) {
        setInputError(validationResult);
        return;
      }

      const formData = new FormData();
      formData.append("loan_id", evaluationDetails?.loan_details?.uuid);
      formData.append("account_holder_name", inputValue?.account_holder_name);
      formData.append("bank", inputValue?.bank?.value);
      formData.append("city", inputValue?.city?.value);
      formData.append("account_number", inputValue?.account_number);
      formData.append("swift_code", inputValue?.swift_code);
      formData.append("status", "under_review");
      formData.append("iban_verified", verificationStatus === 'success');

      // Add the raw verification response as JSON string
      if (inputValue.iban_verification_response) {
        formData.append(
          "iban_verification_response",
          JSON.stringify(inputValue.iban_verification_response)
        );
      }

      if (!!inputValue['bank_account_proof'] &&
        typeof inputValue['bank_account_proof'] !== "string"
      ) {
        formData.append('bank_account_proof', inputValue['bank_account_proof']);
      }

      if (bankAccountDetails?.id) {
        await handleUpdate(formData, bankAccountDetails?.id);
      } else {
        await handleCreate(formData);
      }
    } finally {
      setIsLoading(false);
      submittingRef.current = false;
    }
  };

  const handleCreate = async (data) => {
    const resp = await createBankAccountRequest(data);
    if (resp?.success === true || resp?.status === 201) {
      toast.success("Bank account details submitted successfully.", {
        position: "top-center",
      });
      onHide();
      setShowSuccessModal(true);
    } else {
      toast.error(
        (typeof resp?.error === "string" && resp.error) ||
          resp?.message ||
          resp?.results?.message ||
          "Failed to save bank details. Please try again.",
        { position: "top-center" }
      );
    }
  };

  const handleUpdate = async (data, id) => {
    const resp = await updateBankAccountRequest(data, id);
    if (resp?.success === true || resp?.status === 200) {
      toast.success("Bank account details submitted successfully.", {
        position: "top-center",
      });
      onHide();
    } else {
      toast.error(
        (typeof resp?.error === "string" && resp.error) ||
          resp?.message ||
          resp?.results?.message ||
          "Failed to save bank details. Please try again.",
        { position: "top-center" }
      );
    }
  };

  const handleOnChange = async (name, value) => {
    const stateObj = { ...inputValue, [name]: value };
    setInputValue(stateObj);

    // Reset verification status if IBAN changes
    if (name === 'account_number' && verificationStatus) {
      setVerificationStatus(null);
    }

    if (!!inputError) {
      if (isObjectValueEmpty(inputError)) {
        const error = await checkFormError(stateObj, bankAccountSchema);
        setInputError(error);
      }
    }
  };

  return (
    <>
      <Modal
        className={"anvKyc_tpModal " + (showSuccessModal ? "success_modal" : "")}
        show={showModal}
        onHide={onHide}
        backdrop="static"
        keyboard={false}
        size="xl"
        centered
        aria-labelledby="contained-modal-title-vcenter"
        dialogClassName="modal-90h"
      >
        <Modal.Header closeButton>
          <Modal.Title id="contained-modal-title-vcenter">
            Bank Account Details
          </Modal.Title>
        </Modal.Header>
        <form onSubmit={onSubmitDetails}>
          <Modal.Body className="kycProc_otrBdy p-4">
            <div className="kycProc_sdContent w-100 sm:mx-5 sm:my-3">
              <div className="kycContent_ht">
                <div className="_innerKyc_grid">
                  <div className="_inKycHead">
                    <h1>Offer Details</h1>
                  </div>
                  <div className="_inFr_flexBx anvBas_select">
                    <FloatingField
                      controlId="floatingInput"
                      label="Amount"
                      labelClass=""
                      type="text"
                      placeholder="Offer Amount"
                      name="offer_amount"
                      disabled
                      value={`${evaluationDetails?.amount} AED`}
                    />
                  </div>
                  <iframe
                    title="Offer Letter"
                    src={`${evaluationDetails?.offer_letter}#zoom=FitH`}
                    width={"100%"}
                    height={"600px"}
                  />

                  <hr />
                  <div className="_inKycHead">
                    <h1>Bank Details:</h1>
                    <p>Please add and upload the Bank Account Details which are under the name <b>{companyName}</b> only.</p>
                  </div>

                  {/* IBAN Field with Verification Button (only shown for new accounts) */}
                  <div className="_inFr_flexBx anvBas_select">
                    <div className="w-100 position-relative">
                      <FloatingField
                        controlId="floatingIban"
                        label="IBAN*"
                        labelClass=""
                        type="text"
                        disabled={formDisabled}
                        placeholder="Enter IBAN Number"
                        name="account_number"
                        error={
                          inputError?.account_number
                            ? inputError?.account_number
                            : inputError?.["account_number.value"]
                        }
                        focus={
                          !!inputError?.["account_number.value"] || !!inputError?.account_number
                        }
                        onChange={({ target: { name, value } }) =>
                          handleOnChange(name, value)
                        }
                        value={inputValue.account_number}
                      />
                      {
                        !bankAccountDetails?.id && !formDisabled && (
                          <Button
                            variant="primary"
                            size="sm"
                            className="mt-2"
                            onClick={verifyBankAccount}
                            disabled={isVerifying || !inputValue.account_number}
                          >
                            {isVerifying ? (
                              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                            ) : (
                              "Verify IBAN"
                            )}
                          </Button>
                        )
                      }
                      {verificationStatus === 'success' && (
                        <div className="text-success small mt-1">IBAN verified successfully</div>
                      )}
                      {verificationStatus === 'failed' && (
                        <div className="text-danger small mt-1">IBAN verification failed</div>
                      )}
                    </div>
                  </div>

                  {/* Account Holder Name Field */}
                  <div className="_inFr_flexBx anvBas_select">
                    <FloatingField
                      controlId="floatingAccountHolder"
                      label="Account Holder Name*"
                      labelClass=""
                      type="text"
                      placeholder="Account Holder Name"
                      name="account_holder_name"
                      disabled={formDisabled}
                      error={
                        inputError?.account_holder_name
                          ? inputError?.account_holder_name
                          : inputError?.["account_holder_name.value"]
                      }
                      focus={
                        !!inputError?.["account_holder_name.value"] || !!inputError?.account_holder_name
                      }
                      onChange={({ target: { name, value } }) =>
                        handleOnChange(name, value)
                      }
                      value={inputValue.account_holder_name}
                    />
                  </div>

                  {/* Other Bank Details Fields */}
                  <div className="_inFr_flexBx anvBas_select">
                    <SelectField
                      boxClass="basic-single"
                      classNamePrefix="select"
                      placeholder="Bank*"
                      valueText="id"
                      labelText="name"
                      disabled={formDisabled}
                      options={bankList}
                      name="bank"
                      error={
                        inputError?.bank
                          ? inputError?.bank
                          : inputError?.["bank.value"]
                      }
                      focus={
                        !!inputError?.["bank.value"] || !!inputError?.bank
                      }
                      selectedOption={inputValue.bank}
                      setSelectedOption={(value) =>
                        handleOnChange("bank", value)
                      }
                    />
                    <SelectField
                      boxClass="basic-single"
                      classNamePrefix="select"
                      placeholder="City*"
                      valueText="id"
                      labelText="name"
                      options={cityList}
                      disabled={formDisabled}
                      name="city"
                      error={
                        inputError?.city
                          ? inputError?.city
                          : inputError?.["city.value"]
                      }
                      focus={
                        !!inputError?.["city.value"] || !!inputError?.city
                      }
                      selectedOption={inputValue.city}
                      setSelectedOption={(value) =>
                        handleOnChange("city", value)
                      }
                    />
                    <FloatingField
                      controlId="floatingSwiftCode"
                      label="Swift Code*"
                      labelClass=""
                      type="text"
                      placeholder="Swift Code"
                      disabled={formDisabled}
                      name="swift_code"
                      error={
                        inputError?.swift_code
                          ? inputError?.swift_code
                          : inputError?.["swift_code.value"]
                      }
                      focus={
                        !!inputError?.["swift_code.value"] || !!inputError?.swift_code
                      }
                      onChange={({ target: { name, value } }) =>
                        handleOnChange(name, value)
                      }
                      value={inputValue.swift_code}
                    />
                  </div>

                  {/* Proof Upload Section */}
                  <div className="_inFr_flexBx">
                    <div className="fileAdd_bx">
                      <p>Bank Account Proof{requireProof ? '*' : ''}</p>
                      {requireProof && (
                        <div className="alert alert-warning small mb-2">
                          Please upload proof of account ownership.
                        </div>
                      )}
                      <div
                        className={
                          (inputError?.bank_account_proof ? "error " : "") + "_attachBx"
                        }
                      >
                        <DropzoneField
                          title="Bank Account Proof"
                          htmlFor="attach"
                          disabled={formDisabled}
                          value={
                            !!inputValue.bank_account_proof &&
                              typeof inputValue.bank_account_proof === "string"
                              ? inputValue.bank_account_proof
                              : null
                          }
                          handleOnChange={(value) =>
                            handleOnChange("bank_account_proof", value)
                          }
                        />
                      </div>
                      <span className="fil_addBottom_para">
                        (Upload IBAN certificate or a cancelled cheque for us to verify the details.)
                      </span>

                      {!!inputError?.bank_account_proof && (
                        <p className="invalid-feedback">
                          {inputError?.bank_account_proof}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <div className={inputError?.resError && "anvFlex-root"}>
              {inputError?.resError && (
                <p className="invalid-feedback">
                  {showResponseError().map((item) => item)}
                </p>
              )}

              <Button
                variant="primary"
                type="submit"
                className="anvSv_btn"
                disabled={isLoading}
              >
                <span className="d-flex align-items-center gap-2">
                  <span className="text-base">Save & Continue</span>
                  {isLoading ? (
                    <Loader className="loader_kycBtn" />
                  ) : (
                    <img src={arrImg} className="arrCon_btn" />
                  )}
                </span>
              </Button>
            </div>
          </Modal.Footer>
        </form>
      </Modal>
    </>
  );
};
